/**
 * C2 — "Portfolios you are part of", and the summary strip under it.
 *
 * Renamed from SeatSummary with Story #5648 build v2. It used to state a
 * licensing resolution ("your set tools come from the most generous seat any
 * sponsor pays for") — a sentence about who pays for what. Nothing on this
 * screen is about who pays for anything. THE RULE: the role a person is acting
 * as decides which zoom levels they get; what fills each level is what they own
 * plus what has been shared with them. Being part of a portfolio is a
 * relationship, and it shows an organisation nothing at all.
 *
 * So every card states the same three things the figure states, in the figure's
 * own words: how you came to be here, what you have actually shared, and how to
 * leave. The card is deliberately dense with reassurance because the question it
 * answers — "what did I just let them see?" — has exactly one right answer
 * ("only what you granted") and it has to be visible without clicking anything.
 *
 * Presentation only: the page owns the fetching and the mutations, so this file
 * can be tested with real functions and fixtures, no mocking of any kind.
 */
import { useState } from 'react'
import ExtendedVisibilityRow from './ExtendedVisibilityRow'
import { shortDate } from './sharingModel'
import type { PortfolioRelationship } from './sharingModel'

export interface PortfoliosYoureInProps {
  portfolios: PortfolioRelationship[]
  /** false while the underlying reads are still in flight. */
  loaded: boolean
  /**
   * Distinguishes "read succeeded, there are none" from "the read failed".
   * On this screen an error rendered as an absence of sharing is the most
   * dangerous possible lie, so the two never share a rendering.
   */
  error?: boolean
  onAccept: (aggregatorId: string) => Promise<void>
  onDecline: (aggregatorId: string) => Promise<void>
  onLeave: (aggregatorId: string) => Promise<void>
  onEnableExtendedVisibility: (aggregatorId: string) => Promise<void>
  /** Total projects the caller owns, and how many at least one portfolio sees. */
  sharedProjects: { shared: number; total: number }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

function Card({ rel, onAccept, onDecline, onLeave, onEnableExtendedVisibility }: {
  rel: PortfolioRelationship
  onAccept: (id: string) => Promise<void>
  onDecline: (id: string) => Promise<void>
  onLeave: (id: string) => Promise<void>
  onEnableExtendedVisibility: (id: string) => Promise<void>
}) {
  // Every one of these three writes can be refused, and a refusal that shows
  // nothing is the worst outcome on this card: the reader clicks "Leave this
  // portfolio", sees no change, and cannot tell whether they left. So each runs
  // through here, and a failure says so where the button is.
  const [busy, setBusy] = useState<null | 'accept' | 'end'>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const run = async (what: 'accept' | 'end', action: () => Promise<void>) => {
    setBusy(what)
    setErrorMessage(null)
    try {
      await action()
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (what === 'accept'
          ? 'Could not accept this invitation.'
          : 'Could not change this. Nothing has changed about your sharing.')
      setErrorMessage(message)
    } finally {
      setBusy(null)
    }
  }

  const invitedOn = shortDate(rel.invitedAt)
  const acceptedOn = shortDate(rel.acceptedAt)
  const accepted = rel.state === 'accepted'
  return (
    <div
      data-portfolio={rel.aggregatorId}
      className="rounded-xl border border-slate-200 bg-white px-3.5 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[14px] font-bold text-slate-900">{rel.organisation}</h3>
        {accepted ? (
          <span className="rounded-full bg-iflow-100 px-2 py-0.5 text-[11px] font-semibold text-iflow-700">
            ★ You are part of this portfolio
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            Invited · not yet accepted
          </span>
        )}
      </div>

      {/* The figure's line is "Invited by Meridian Ventures 12 Mar 2026 ·
          accepted 14 Mar 2026" — BOTH dates. Where a date is genuinely not on
          the record the card says so, in those words, rather than trailing off
          into a bare "accepted".

          It is worth being precise about why that case exists, because it is
          data and not code: measured in the aggregator-entities table on 6 Sep
          2026, charlie's four rows carry sponsor_started_at and NO invited_at
          or accepted_at, and bob's live row carries none of the three — those
          relationships were seeded, not created through the invite flow, which
          is the only path that writes the two timestamps. bob's ENDED row, made
          the real way, has both. So the two-date line renders in full for any
          relationship that was actually invited and accepted, and says what is
          missing for the seeded ones instead of inventing a date. */}
      <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
        Invited by <b className="text-slate-900">{rel.organisation}</b>
        {invitedOn
          ? <> <b className="text-slate-900">{invitedOn}</b></>
          : <span className="text-slate-400"> (invitation date not recorded)</span>}
        {' · '}
        {accepted ? (
          acceptedOn
            ? <>accepted <b className="text-slate-900">{acceptedOn}</b></>
            : <>accepted <span className="text-slate-400">(date not recorded)</span></>
        ) : (
          <b className="text-slate-900">not yet accepted</b>
        )}
      </p>

      <p className="mt-0.5 text-[12px] leading-relaxed text-slate-600">
        You share: <b className="text-slate-900">{plural(rel.projectsShared, 'project', 'projects')}</b>
        {' · '}
        <b className="text-slate-900">{plural(rel.foldersHidden, 'folder hidden', 'folders hidden')}</b>
      </p>

      {accepted ? (
        <p className="mt-1 text-[11.5px] leading-relaxed text-slate-500">
          Accepting made you part of their portfolio and granted them nothing —
          every project they see below is one you granted, and you can revoke any
          of them at any time.
        </p>
      ) : (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] leading-relaxed text-slate-500">
          <span className="min-w-0 flex-1">
            Nothing from this invitation applies until you accept — the{' '}
            {plural(rel.projectsShared, 'project', 'projects')} above{' '}
            {rel.projectsShared === 1 ? 'is a grant' : 'are grants'} you made yourself
          </span>
          <button
            type="button"
            onClick={() => run('accept', () => onAccept(rel.aggregatorId))}
            disabled={busy !== null}
            className="shrink-0 rounded border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy === 'accept' ? 'Accepting…' : 'Accept'}
          </button>
        </div>
      )}

      <ExtendedVisibilityRow
        organisation={rel.organisation}
        isOn={rel.extendedVisibility}
        onEnable={() => onEnableExtendedVisibility(rel.aggregatorId)}
      />

      <div className="mt-2 flex items-center gap-3 border-t border-slate-100 pt-2 text-[11.5px] leading-relaxed text-slate-500">
        <span className="min-w-0 flex-1">
          {accepted
            ? 'Leaving keeps everything yours — the grants you made stay until you revoke them.'
            : 'Declining ends the invitation — the grants you made stay until you revoke them.'}
        </span>
        <button
          type="button"
          onClick={() => run('end', () => (accepted
            ? onLeave(rel.aggregatorId)
            : onDecline(rel.aggregatorId)))}
          disabled={busy !== null}
          className="shrink-0 whitespace-nowrap rounded border border-rose-200 px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
        >
          {busy === 'end'
            ? (accepted ? 'Leaving…' : 'Declining…')
            : (accepted ? 'Leave this portfolio' : 'Decline invitation')}
        </button>
      </div>

      {errorMessage && (
        <p className="mt-1.5 text-[11.5px] text-rose-600">{errorMessage}</p>
      )}
    </div>
  )
}

/** The summary strip: what the two lists add up to, in one sentence each. */
function SummaryStrip({ portfolios, sharedProjects }: {
  portfolios: PortfolioRelationship[]
  sharedProjects: { shared: number; total: number }
}) {
  const described = portfolios.map((rel) => {
    const when = shortDate(rel.state === 'accepted' ? rel.acceptedAt : rel.invitedAt)
    if (rel.state === 'accepted') {
      return when ? `${rel.organisation} (accepted ${when})` : rel.organisation
    }
    return when
      ? `${rel.organisation} (invited ${when}, not yet accepted)`
      : `${rel.organisation} (not yet accepted)`
  })
  const list =
    described.length === 0
      ? ''
      : described.length === 1
        ? described[0]
        : `${described.slice(0, -1).join(', ')} and ${described[described.length - 1]}`

  return (
    <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="min-w-[280px] flex-1 text-[12.5px] leading-relaxed text-slate-600">
        <b className="text-slate-900">Portfolios you&apos;re in: {portfolios.length}</b>
        {list ? ` — ${list}.` : ' — none yet.'} Being part of a portfolio changes
        nothing about your own account, and never narrows what you see of your
        own inventions.
      </p>
      <p className="ml-auto text-[12.5px] text-slate-600">
        <b className="text-slate-900">Shared with at least one portfolio:</b>{' '}
        {sharedProjects.shared} of your {sharedProjects.total}{' '}
        {sharedProjects.total === 1 ? 'project' : 'projects'}
      </p>
    </div>
  )
}

export default function PortfoliosYoureIn({
  portfolios, loaded, error, onAccept, onDecline, onLeave,
  onEnableExtendedVisibility, sharedProjects,
}: PortfoliosYoureInProps) {
  return (
    // The heading is INSIDE the part's own root, and so is every state beneath
    // it. The figure's structure does not depend on the data: an inventor in no
    // portfolio still sees "Portfolios you are part of · 0" and an honest line,
    // because a section that vanishes when it is empty leaves the reader unable
    // to tell "nothing here" from "this screen forgot to load".
    <section data-part="PortfoliosYoureIn" aria-label="Portfolios you are part of" className="mb-2">
      <h2 className="mb-2 flex flex-wrap items-center gap-2 text-[14px] font-bold text-slate-900">
        Portfolios you are part of
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">
          {loaded && !error ? portfolios.length : '—'}
        </span>
        <span className="text-[11.5px] font-medium text-slate-500">
          · being part of a portfolio never shows them your work — sharing is
          yours to give, project by project
        </span>
      </h2>

      {!loaded && !error && (
        <p className="mb-3 text-sm text-slate-400">Loading your portfolios…</p>
      )}
      {error && (
        <p className="mb-3 text-sm text-rose-600">
          Could not load your portfolios — nothing here is a statement about your
          sharing until it loads.
        </p>
      )}
      {loaded && !error && portfolios.length === 0 && (
        <p className="mb-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-600">
          No portfolios yet. Nobody has invited you to one, and you are part of
          none — which changes nothing about what you can see of your own
          inventions.
        </p>
      )}

      {loaded && !error && portfolios.length > 0 && (
        <div className="mb-3 grid gap-3 lg:grid-cols-2">
          {portfolios.map((rel) => (
            <Card
              key={rel.aggregatorId}
              rel={rel}
              onAccept={onAccept}
              onDecline={onDecline}
              onLeave={onLeave}
              onEnableExtendedVisibility={onEnableExtendedVisibility}
            />
          ))}
        </div>
      )}

      {loaded && !error && (
        <SummaryStrip portfolios={portfolios} sharedProjects={sharedProjects} />
      )}
    </section>
  )
}
